import React, { useRef, useState } from 'react';
import { onboardingAPI } from '../../services/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CheckCircle, Camera, ArrowsClockwise, IdentificationCard } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const DocumentRow = ({ token, doc, onUploaded }) => {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);
  const uploaded = doc.status === 'uploaded';

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setProgress(0);
    try {
      await onboardingAPI.publicUploadDocument(token, doc.doc_type_id, file, setProgress);
      toast.success(`${doc.name} uploaded`);
      onUploaded?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed, please try again'); }
    finally { setProgress(null); }
  };

  return (
    <Card className={`rounded-2xl ${uploaded ? 'bg-white/70' : 'hover:shadow-md transition-shadow'}`} data-testid={`packet-doc-${doc.doc_type_id}`}>
      <CardContent className="p-4 sm:p-5 flex items-center gap-4">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 overflow-hidden ${uploaded ? 'bg-emerald-100 text-emerald-700' : 'bg-primary/10 text-primary'}`}>
          {uploaded && doc.content_type?.startsWith('image/') ? <img src={`${onboardingAPI.publicDocumentUrl(token, doc.doc_type_id)}?t=${encodeURIComponent(doc.uploaded_at || '')}`} alt={doc.name} className="w-full h-full object-cover" />
            : uploaded ? <CheckCircle weight="fill" className="w-6 h-6" /> : <IdentificationCard weight="duotone" className="w-6 h-6" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{doc.name} {!doc.required && <Badge variant="secondary" className="rounded-full text-[10px] ml-1">Optional</Badge>}</div>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">
            {progress !== null ? `Uploading… ${progress}%` : uploaded ? `Uploaded ${doc.uploaded_at ? format(new Date(doc.uploaded_at), 'MMM d, yyyy') : ''} · ${doc.filename}` : doc.description || 'Take a photo or upload a PDF'}
          </p>
        </div>
        <input ref={inputRef} type="file" accept="image/*,application/pdf" capture="environment" className="hidden" onChange={handleFile} data-testid={`doc-upload-input-${doc.doc_type_id}`} />
        {uploaded ? (
          <div className="flex items-center gap-1 shrink-0">
            <Button variant="outline" size="sm" className="rounded-xl" asChild><a href={onboardingAPI.publicDocumentUrl(token, doc.doc_type_id)} target="_blank" rel="noreferrer" data-testid={`view-doc-${doc.doc_type_id}`}>View</a></Button>
            <Button variant="ghost" size="sm" className="rounded-xl h-9 w-9 p-0" title="Replace" onClick={() => inputRef.current?.click()} disabled={progress !== null} data-testid={`replace-doc-${doc.doc_type_id}`}><ArrowsClockwise className="w-4 h-4" /></Button>
          </div>
        ) : (
          <Button size="sm" className="rounded-xl gap-1 shrink-0" onClick={() => inputRef.current?.click()} disabled={progress !== null} data-testid={`upload-doc-${doc.doc_type_id}`}><Camera weight="bold" className="w-4 h-4" /> Upload</Button>
        )}
      </CardContent>
    </Card>
  );
};

const DocumentUploadList = ({ token, documents, onUploaded }) => {
  if (!documents?.length) return null;
  const done = documents.filter(d => d.status === 'uploaded').length;
  return (
    <div className="space-y-3" data-testid="document-upload-list">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-lg font-semibold">Documents</h2>
        <span className="text-xs text-muted-foreground tabular-nums">{done}/{documents.length} uploaded</span>
      </div>
      {documents.map(d => <DocumentRow key={d.doc_type_id} token={token} doc={d} onUploaded={onUploaded} />)}
    </div>
  );
};

export default DocumentUploadList;
